import React, { useMemo } from "react";
import { BarChart } from "@mui/x-charts/BarChart";
import dayjs from "dayjs";

import { DashboardEmptyState } from "../../components";
import { StyledMultiLineStack } from "./styles";

const PALETTE = [
  "#16a34a",
  "#3478F6",
  "#6f5af0",
  "#d97706",
  "#dc2626",
  "#0891b2",
  "#7c3aed",
  "#65a30d",
  "#db2777",
  "#475569",
];

const colorFor = (departmentId, index) =>
  PALETTE[Number(departmentId) % PALETTE.length] ||
  PALETTE[index % PALETTE.length];

// Backend's `groupBy` tells us how to format each bucket's label —
// hard-coding "MMM" collapses day/week buckets into duplicate strings
// and breaks the band axis.
const LABEL_FORMAT = {
  day: "MMM D",
  week: "MMM D",
  month: "MMM",
};

const DashboardLaborChart = ({ data, isLoading }) => {
  const { labels, series, total } = useMemo(() => {
    // `buckets` is the new shape (day/week/month); fall back to
    // `months` for any cached responses from before the rename.
    const bucketRows = data?.buckets || data?.months || [];
    const list = data?.series || [];
    const fmt = LABEL_FORMAT[data?.groupBy] || LABEL_FORMAT.day;
    const lbls = bucketRows.map((b) => dayjs(b.date).format(fmt));
    // All series share the same stack key so they pile into one bar per month.
    const built = list.map((s, i) => ({
      label: s.department || "Unknown",
      data: s.data,
      color: colorFor(s.departmentId, i),
      stack: "labor",
    }));
    const tot = list.reduce(
      (sum, s) => sum + (s.data || []).reduce((a, v) => a + (v || 0), 0),
      0,
    );
    return { labels: lbls, series: built, total: tot };
  }, [data]);

  if (!isLoading && total === 0) {
    return (
      <DashboardEmptyState
        icon="Clock"
        title="No labor activity"
        subtitle="Non-admin labor tickets will stack here by department over the last 12 months."
      />
    );
  }

  return (
    <StyledMultiLineStack>
      <BarChart
        height={300}
        series={series}
        xAxis={[{ scaleType: "band", data: labels }]}
        yAxis={[{ valueFormatter: (v) => (v ? v.toLocaleString() : "0") }]}
        margin={{ top: 10, bottom: 30, left: 50, right: 10 }}
        grid={{ horizontal: true }}
        borderRadius={4}
        slotProps={{
          legend: {
            direction: "row",
            position: { vertical: "bottom", horizontal: "middle" },
            itemMarkWidth: 8,
            itemMarkHeight: 8,
            labelStyle: { fontSize: 10 },
          },
        }}
      />
    </StyledMultiLineStack>
  );
};

export default DashboardLaborChart;
